// Client-side sorting for an in-memory table. Promoted verbatim from admin's
// `lib/table-sort.ts` (the pattern is proven, zero admin-specific coupling).
//
// Every table using this already holds its whole page of rows in memory, so
// sorting is a local recompute rather than a refetch — one hook, no network,
// no server contract to extend.
//
// Sort keys are declared per table as a map from column id to a value
// EXTRACTOR, not as a field name. That is what lets a column sort on
// something other than what it prints.
import { useCallback, useMemo } from "react";

import { useRestoredState } from "./view-state";

/** What a column sorts on. `null`/`undefined` always sort LAST, in both
 *  directions — a missing value is not "smallest", it is unknown, and burying it
 *  under real data in one direction only would be arbitrary. */
export type SortValue = string | number | boolean | null | undefined;

export type SortDirection = "asc" | "desc";

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

/** Column id → how to read that column's sort value off a row. */
export type SortAccessors<T, K extends string> = Record<K, (row: T) => SortValue>;

/** Is this value absent? Empty string counts: a blank cell and a null one read
 *  the same to a user, so they sort the same. */
function isMissing(value: SortValue): boolean {
  return value === null || value === undefined || value === "";
}

/** Compare two PRESENT values. Numbers and booleans compare naturally; strings
 *  compare case-insensitively with `localeCompare`, so "Ada" and "ada" land
 *  together, accented names order sensibly, and "item 2" precedes "item 10". */
function comparePresent(a: SortValue, b: SortValue): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
}

/** Order two rows' values for a given direction.
 *
 *  Missing is handled BEFORE the direction is applied, which is the whole point:
 *  a missing value is unknown, not "smallest", so reversing the sort must not
 *  drag every blank row to the top. Only the present-vs-present comparison is
 *  flipped. */
function compareForDirection(a: SortValue, b: SortValue, factor: 1 | -1): number {
  const aMissing = isMissing(a);
  const bMissing = isMissing(b);
  if (aMissing || bMissing) return Number(aMissing) - Number(bMissing);
  return comparePresent(a, b) * factor;
}

/**
 * The next sort state when `key`'s header is clicked: asc → desc → off.
 *
 * Three-state so a user can always get back to the table's natural order.
 * Picking a DIFFERENT column always restarts at ascending rather than
 * inheriting the previous column's direction.
 *
 * Exported and pure so the state machine is testable without rendering.
 */
export function nextSortState<K extends string>(
  current: SortState<K> | null,
  key: K,
): SortState<K> | null {
  if (current?.key !== key) return { key, direction: "asc" };
  if (current.direction === "asc") return { key, direction: "desc" };
  return null;
}

/**
 * `rows` ordered by `sort`. Returns a COPY; the input is never mutated, because
 * these arrays typically come straight out of the React Query cache.
 *
 * Sorting is stable, so rows that compare equal keep their incoming order rather
 * than shuffling between renders.
 */
export function sortRows<T, K extends string>(
  rows: readonly T[],
  accessors: SortAccessors<T, K>,
  sort: SortState<K> | null,
): T[] {
  const copy = [...rows];
  if (!sort) return copy;
  const read = accessors[sort.key];
  const factor: 1 | -1 = sort.direction === "asc" ? 1 : -1;
  return copy.sort((a, b) => compareForDirection(read(a), read(b), factor));
}

export interface UseTableSortResult<T, K extends string> {
  /** Rows in the current sort order. Input order is preserved when unsorted. */
  rows: T[];
  /** The active sort, or null when the table is in its natural order. */
  sort: SortState<K> | null;
  /** Click a header: sorts ascending, flips to descending, then clears. */
  toggle: (key: K) => void;
  /** `aria-sort` value for a header cell. */
  ariaSort: (key: K) => "ascending" | "descending" | "none";
}

export interface TableSortOptions {
  /** Opt this table's sort into surviving a history BACK hop, under a key unique
   *  across the app (`view-state.ts`). Omit and the sort is plain local state. */
  restore?: string;
}

/**
 * Sort `rows` by whichever column the user picked.
 *
 * Three-state per column (asc → desc → off) so a user can always get back to
 * the table's natural order.
 */
export function useTableSort<T, K extends string>(
  rows: readonly T[],
  accessors: SortAccessors<T, K>,
  initial: SortState<K> | null = null,
  options: TableSortOptions = {},
): UseTableSortResult<T, K> {
  const [sort, setSort] = useRestoredState<SortState<K> | null>(options.restore, initial);

  // `setSort` is a `useState` setter and therefore stable; it is in the deps
  // because it now arrives through a wrapper hook, where eslint cannot see that.
  const toggle = useCallback(
    (key: K) => {
      setSort((current) => nextSortState(current, key));
    },
    [setSort],
  );

  const sorted = useMemo(() => sortRows(rows, accessors, sort), [rows, sort, accessors]);

  const ariaSort = useCallback(
    (key: K): "ascending" | "descending" | "none" => {
      if (sort?.key !== key) return "none";
      return sort.direction === "asc" ? "ascending" : "descending";
    },
    [sort],
  );

  return { rows: sorted, sort, toggle, ariaSort };
}

/** Case-insensitive "does any of these fields contain the query" match, the
 *  filter every table box uses. An empty query matches everything. */
export function matchesQuery(query: string, ...fields: (string | null | undefined)[]): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return fields.some((f) => (f ?? "").toLowerCase().includes(needle));
}
